import { IntlShape } from 'react-intl';
import { ServiceStatisticsSummaryDTO } from '../../dto/ServiceStatisticsDTO';
import { formatDecimal, formatPercent, formatShortDate, parseIsoDate, periodNeedsYear, relativeChange } from './statisticsUtils';

/** Hacia dónde se movió el indicador contra el período anterior. */
export type Trend = 'up' | 'down' | 'same';

/** Si el cambio es bueno o malo, solo en los indicadores donde subir o bajar tiene ese sentido (la espera). */
export type Verdict = 'better' | 'worse' | null;

/** Una variación ya redondeada como se muestra: lo que se redondea a cero es «igual», nunca "0 %". */
export interface Variation {
    trend: Trend;
    /** "8 %" o "2,1 puntos": sin signo, la dirección va en `trend`. Vacío si es «igual». */
    value: string;
}

/** Variación relativa en % (entero). Null si no hay con qué comparar. */
export function percentVariation(current: number, previous: number | null | undefined): Variation | null {
    const change = relativeChange(current, previous);
    if (change === null) return null;
    if (Math.round(Math.abs(change) * 100) === 0) return { trend: 'same', value: '' };
    return { trend: change > 0 ? 'up' : 'down', value: formatPercent(Math.abs(change)) };
}

/** Variación en puntos porcentuales entre dos proporciones (fracciones), con un decimal. */
export function pointsVariation(intl: IntlShape, current: number | null, previous: number | null): Variation | null {
    if (current === null || previous === null) return null;
    const points = Math.round(Math.abs(current - previous) * 1000) / 10;
    if (points === 0) return { trend: 'same', value: '' };
    return {
        trend: current > previous ? 'up' : 'down',
        value: intl.formatMessage({ id: 'ServicesStatisticsPointsValue' }, { value: formatDecimal(points) }),
    };
}

/** Para la espera, subir es peor y bajar es mejor. */
export const waitVerdict = (trend: Trend): Verdict => (trend === 'up' ? 'worse' : trend === 'down' ? 'better' : null);

/** "26/8–24/9": el período anterior con que se compara, o el texto genérico si el backend no manda las fechas. */
export function previousPeriodLabel(intl: IntlShape, summary: ServiceStatisticsSummaryDTO, today: Date): string {
    if (!summary.previousInitialDate || !summary.previousFinalDate) {
        return intl.formatMessage({ id: 'ServicesStatisticsPreviousPeriod' });
    }
    const from = parseIsoDate(summary.previousInitialDate);
    const to = parseIsoDate(summary.previousFinalDate);
    const withYear = periodNeedsYear(from, to, today);
    return `${formatShortDate(from, withYear)}–${formatShortDate(to, withYear)}`;
}
